import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik';
import { FloatingLabel, Form, Spinner } from 'react-bootstrap';
import { IoIosArrowRoundBack, IoMdClose } from 'react-icons/io';
import { newticketSchema } from 'validation-schema/ticket-schema';
import { showToastError, showToastSuccess } from 'components/toastify/ToastNotifications';
import { useAddTicketMutation, useGetAllCategoriesQuery } from 'api/requestApi';
import { useGetUsersQuery } from 'api/userManagementApi';
import { ItemModel } from 'model/users-model';
import { Priority, TicketCategoryModel } from 'model/request-model';
import { BiSolidCloudUpload } from 'react-icons/bi';

type Props = {
    handleReturn: () => void;
}

const NewTicket = ({ handleReturn }: Props) => {

    const [loading, setLoading] = useState(false);

    const { data: usersData, error: usersError, isFetching: usersIsFetching, refetch: refetchUser } = useGetUsersQuery();
    const { data: categories, error: categoriesError, isFetching: catIsFetching, refetch: catRefetch } = useGetAllCategoriesQuery();

    const [adminUsers, setAdminUsers] = useState<ItemModel>();
    const [allcategories, setAllCategories] = useState<TicketCategoryModel[]>([]);

    useEffect(() => {
        if (usersIsFetching) {
            setLoading(true);
        }
        else {
            setLoading(false);
            if (usersError) {
                showToastError('Unable to fetch data from the server');
            }
            else {
                setAdminUsers(usersData?.data as ItemModel);
            }
        }
    }, [usersIsFetching]);

    useEffect(() => {
        if (catIsFetching) {
            setLoading(true);
        }
        else {
            setLoading(false);
            if (categoriesError) {
                showToastError('Unable to fetch data from the server');
            }
            else {
                setAllCategories(categories?.data as TicketCategoryModel[]);
            }
        }
    }, [catIsFetching]);

    const [isDragOver, setIsDragOver] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileUpload(e.dataTransfer.files);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        handleFileUpload(files);
    };

    const handleFileUpload = (files: FileList | null) => {
        if (files && files.length > 0) {
            const newAttachments: File[] = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const reader = new FileReader();
                reader.onload = (event) => {
                    if (event.target && event.target.result) {
                        const blob = new Blob([event.target.result], { type: file.type });
                        const fileWithData = new File([blob], file.name, { type: file.type });
                        newAttachments.push(fileWithData);
                        if (newAttachments.length === files.length) {
                            setAttachments([...attachments, ...newAttachments]);
                        }
                    }
                };
                reader.readAsArrayBuffer(file);
            }
        }
    };

    const openFileDialog = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const removeAttachment = (indexToRemove: number) => {
        setAttachments(prevAttachments => prevAttachments.filter((_, index) => index !== indexToRemove));
    };

    const [addTicket, addTicketResult] = useAddTicketMutation();

    const formik = useFormik({
        initialValues: {
            customerName: '',
            customerEmail: '',
            categoryId: '',
            assignedUserId: '',
            priority: Priority?.LOW,
            subject: '',
            message: '',
        },
        validationSchema: newticketSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const formData = new FormData();
                formData.append('customerName', values?.customerName);
                formData.append('customerEmail', values?.customerEmail);
                formData.append('categoryId', values?.categoryId);
                formData.append('assignedUserId', values?.assignedUserId);
                formData.append('priority', values?.priority);
                formData.append('subject', values?.subject);
                formData.append('message', values?.message);
                formData.append('IsChatExist', 'false');
                // Append each attachment individually
                attachments.forEach((file, index) => {
                    formData.append(`files`, file);
                });
                const response = await addTicket(formData).unwrap();
                if (response) {
                    showToastSuccess(response?.ResponseMessage);
                    handleReturn();
                }
            }
            catch (error: any) {
                showToastError(error?.data.ResponseMessage)
            }
            finally {
                setLoading(false);
            }
        },
    });

    return (
        <div>
            <div className='row col-lg-6'>
                <div>
                    <button type='button' onClick={() => handleReturn()} className='btn-secondary-outline px-3 py-2'><IoIosArrowRoundBack size={20} /> Go Back</button>
                </div>
                <Form onSubmit={formik.handleSubmit}>
                    <FloatingLabel controlId="floatingInput" label="Enter customers name" className="float-input mt-5 px-0" >
                        <Form.Control type="text" name="customerName" value={formik?.values?.customerName} onChange={formik?.handleChange} onBlur={formik?.handleBlur} placeholder="Enter customers name" />{formik.touched.customerName && formik.errors.customerName && <span className='text-danger font-size-sm'>{formik.errors.customerName}</span>}
                    </FloatingLabel>
                    <FloatingLabel controlId="floatingInput" label="Enter customers email" className="float-input mt-4 px-0" >
                        <Form.Control type="email" name="customerEmail" value={formik?.values?.customerEmail} onChange={formik?.handleChange} onBlur={formik?.handleBlur} placeholder="Enter customers email" />{formik.touched.customerEmail && formik.errors.customerEmail && <span className='text-danger font-size-sm'>{formik.errors.customerEmail}</span>}
                    </FloatingLabel>
                    <FloatingLabel controlId="floatingInput" label="Ticket Category" className="float-input mt-4 px-0" >
                        <Form.Select aria-label="Floating label select example" name="categoryId" value={formik?.values?.categoryId} onChange={formik?.handleChange} onBlur={formik?.handleBlur} className="">
                            <option value=''>Select Category</option>
                            {allcategories?.map((category, key) => <option key={key} value={category?.id}>{category?.categoryName}</option>)}
                        </Form.Select>
                        {formik.touched.categoryId && formik.errors.categoryId && <span className='text-danger font-size-sm'>{formik.errors.categoryId}</span>}
                    </FloatingLabel>
                    <FloatingLabel controlId="floatingInput" label="Assign Ticket To" className="float-input mt-4 px-0" >
                        <Form.Select aria-label="Floating label select example" name="assignedUserId" value={formik?.values?.assignedUserId} onChange={formik?.handleChange} onBlur={formik?.handleBlur} className="">
                            <option value=''>Assign to</option>
                            {adminUsers?.items?.map((users, key) => <option key={key} value={users?.userId}>{users?.firstName} {users?.lastName}</option>)}
                        </Form.Select>
                        {formik.touched.assignedUserId && formik.errors.assignedUserId && <span className='text-danger font-size-sm'>{formik.errors.assignedUserId}</span>}
                    </FloatingLabel>
                    <FloatingLabel controlId="floatingInput" label="Select Priority" className="float-input mt-4 px-0" >
                        <Form.Select aria-label="Floating label select example" name="priority" value={formik?.values?.priority} onChange={formik?.handleChange} onBlur={formik?.handleBlur}>
                            <option value=''>Priority</option>
                            {Object.values(Priority).map((priority) => (
                                <option key={priority} value={priority}>
                                    {priority}
                                </option>
                            ))}
                        </Form.Select>
                        {formik.touched.priority && formik.errors.priority && <span className='text-danger font-size-sm'>{formik.errors.priority}</span>}
                    </FloatingLabel>
                    <FloatingLabel controlId="floatingInput" label="Enter Subject" className="float-input mt-5 px-0" >
                        <Form.Control type="text" name="subject" value={formik?.values?.subject} onChange={formik?.handleChange} onBlur={formik?.handleBlur} placeholder="Enter Subject" />{formik.touched.subject && formik.errors.subject && <span className='text-danger font-size-sm'>{formik.errors.customerName}</span>}
                    </FloatingLabel>
                    <textarea rows={7} name="message" value={formik?.values?.message} onChange={formik?.handleChange} onBlur={formik?.handleBlur} className='input-field w-100 mt-4' placeholder='Enter your message'></textarea>
                    {formik.touched.message && formik.errors.message && <span className='text-danger font-size-sm'>{formik.errors.message}</span>}
                    {/* <textarea rows={7} className='input-field w-100 mt-4' placeholder='Enter your message'></textarea> */}
                    <div
                        className={`my-3 file-upload-container pointer ${isDragOver ? 'drag-over' : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={openFileDialog}
                    >
                        <BiSolidCloudUpload size='20' className="mb-2" />
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            onChange={handleFileInputChange}
                            ref={fileInputRef}
                            multiple
                            accept=".jpg, .png, .jpeg"
                        />
                        <p>Drag and drop a file here, OR <br /> click to choose a file</p>
                        {attachments?.length > 0 && (
                            <div>
                                <p>Uploaded File Details:</p>
                                {attachments?.map((item, index) => <div className='d-inline mx-2'>
                                    <img src={URL.createObjectURL(item)} width='40%' alt="Uploaded File" />
                                    <button type='button' onClick={() => removeAttachment(index)} className="btn btn-danger btn-sm px-1 py-0"><IoMdClose /></button>
                                </div>)}
                            </div>
                        )}
                    </div>
                    <button type='submit' className='btn-primary my-3 w-auto px-4 py-2'>Create Ticket {loading && <Spinner size='sm' />}</button>
                </Form>
            </div>
        </div>
    )
}

export default NewTicket;
